import { TimeInput } from '@tebuto/forms/TimeInput'
import { useComponentTranslation } from '@tebuto/functions/translation.components'
import { Formik } from 'formik'
import { DateTime } from 'luxon'
import { useContext } from 'react'
import { BookingCalendarContext } from '../context'

interface TimeFiltersProps {
    theme?: {
        backgroundColor: string
        textSecondary: string
        borderColor?: string
        primaryColor: string
    }
}

export default function TimeFilters({ theme }: TimeFiltersProps) {
    const { t } = useComponentTranslation()
    const { earliestStart, setEarliestStart, latestEnd, setLatestEnd } = useContext(BookingCalendarContext)

    // Helper function to lighten a color
    const lightenColor = (color: string, amount: number): string => {
        const hex = color.replace('#', '')
        const r = Number.parseInt(hex.substring(0, 2), 16)
        const g = Number.parseInt(hex.substring(2, 4), 16)
        const b = Number.parseInt(hex.substring(4, 6), 16)

        const newR = Math.min(255, Math.floor(r + (255 - r) * amount))
        const newG = Math.min(255, Math.floor(g + (255 - g) * amount))
        const newB = Math.min(255, Math.floor(b + (255 - b) * amount))

        return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`
    }

    // Helper function to get contrast color (white or black)
    const getContrastColor = (backgroundColor: string): string => {
        const hex = backgroundColor.replace('#', '')
        const r = Number.parseInt(hex.substring(0, 2), 16)
        const g = Number.parseInt(hex.substring(2, 4), 16)
        const b = Number.parseInt(hex.substring(4, 6), 16)

        // Calculate relative luminance
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
        return luminance > 0.5 ? '#000000' : '#ffffff'
    }

    // Map widget theme to TimeInput theme interface (only if theme is provided)
    const timeInputTheme = theme
        ? {
              backgroundColor: theme.backgroundColor,
              textColor: theme.textSecondary,
              borderColor: theme.borderColor || '#d1d5db',
              primaryColor: theme.primaryColor,
              primaryColorLight: lightenColor(theme.primaryColor, 0.9), // Generate light variant (90% lighter)
              textOnPrimary: getContrastColor(theme.primaryColor)
          }
        : undefined

    return (
        <div className="space-y-3">
            <div>
                <h4 className="font-medium text-sm widget-text-primary mb-3">{t('booking.bookingCalendar.time')}</h4>

                <Formik initialValues={{ earliestStart, latestEnd }} onSubmit={() => {}}>
                    {() => (
                        <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-3">
                            <TimeInput
                                labelClassName="widget-text-secondary"
                                name={'earliestStart'}
                                value={earliestStart}
                                onChange={(value) => {
                                    if (value?.isValid) {
                                        setEarliestStart(value as DateTime<true>)
                                    }
                                }}
                                label={t('booking.bookingCalendar.earliestStart')}
                                className="w-full widget-border widget-text-primary"
                                theme={timeInputTheme}
                            />
                            <TimeInput
                                labelClassName="widget-text-secondary"
                                name={'latestEnd'}
                                value={latestEnd}
                                onChange={(value) => {
                                    if (value?.isValid) {
                                        setLatestEnd(value as DateTime<true>)
                                    }
                                }}
                                label={t('booking.bookingCalendar.latestEnd')}
                                className="w-full widget-border widget-text-primary"
                                theme={timeInputTheme}
                            />
                        </div>
                    )}
                </Formik>
            </div>
        </div>
    )
}
